import { districtsFor } from "@/lib/districts";
/**
 * «Основа профиля» — 5 пунктов. Город — без срока в Турции. Должно в точности совпадать с SQL profile_core_progress.
 */
export type CoreProgressKey = "photo" | "city" | "contact" | "interests" | "about";

type ProgressInput =
  | {
      photo_url?: string | null;
      city?: string | null;
      time_in_turkey?: string | null;
      telegram?: string | null;
      social_links?: string[] | null;
      interests?: string[] | null;
      profession?: string | null;
      looking_for_tags?: string[] | null;
      coffee_status?: string[] | null;
    }
  | null
  | undefined;

const LABELS: Record<CoreProgressKey, string> = {
  photo: "фото",
  city: "город",
  contact: "Telegram",
  interests: "интересы",
  about: "чем занимаешься",
};

const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;

export function profileCoreProgress(p: ProgressInput): {
  done: number;
  total: 5;
  missing: { key: CoreProgressKey; label: string }[];
} {
  const city = p?.city?.trim() || "Не указан";
  const checks: Record<CoreProgressKey, boolean> = {
    photo: filled(p?.photo_url),
    city: city !== "Не указан",
    contact: filled(p?.telegram) || (p?.social_links ?? []).some((s) => filled(s)),
    interests: (p?.interests?.length ?? 0) >= 3,
    about:
      filled(p?.profession) &&
      ((p?.looking_for_tags?.length ?? 0) > 0 || (p?.coffee_status?.length ?? 0) > 0),
  };
  const keys = Object.keys(checks) as CoreProgressKey[];
  const missing = keys.filter((k) => !checks[k]).map((key) => ({ key, label: LABELS[key] }));
  return { done: keys.length - missing.length, total: 5, missing };
}

/** «фото и Telegram», «фото, город и Telegram» */
export function joinMissing(labels: string[]): string {
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} и ${labels[labels.length - 1]}`;
}

/** «Полный профиль» — только для показа на странице профиля; на подбор/драйвы не влияет. */
export type FullProgressKey = "about" | "topic" | "languages" | "industry" | "district" | "origin";
export type FullProgressSection = "more" | "basic";

type FullInput =
  | {
      about?: string | null;
      conversation_topic?: string | null;
      languages?: string[] | null;
      industries?: string[] | null;
      industry?: string | null;
      district?: string | null;
      city?: string | null;
      visitor_kind?: string | null;
      origin_city?: string | null;
      time_in_turkey?: string | null;
    }
  | null
  | undefined;

const FULL_ITEMS: { key: FullProgressKey; label: string; section: FullProgressSection }[] = [
  { key: "about", label: "рассказ о себе", section: "more" },
  { key: "topic", label: "тема для разговора", section: "more" },
  { key: "languages", label: "языки", section: "basic" },
  { key: "industry", label: "сфера", section: "basic" },
  { key: "district", label: "район", section: "basic" },
  { key: "origin", label: "откуда и сколько в Турции", section: "basic" },
];

export function profileFullProgress(p: FullInput): {
  done: number;
  total: number;
  missing: { key: FullProgressKey; label: string; section: FullProgressSection }[];
} {
  const city = p?.city?.trim() || "Не указан";
  const districtApplies =
    city !== "Не указан" && p?.visitor_kind !== "tourist" && districtsFor(city).length > 0;
  const checks: Record<FullProgressKey, boolean> = {
    about: filled(p?.about),
    topic: filled(p?.conversation_topic),
    languages: (p?.languages ?? []).some((l) => filled(l)),
    industry: (p?.industries ?? []).some((l) => filled(l)) || filled(p?.industry),
    district: filled(p?.district),
    origin: filled(p?.origin_city) && filled(p?.time_in_turkey),
  };
  const items = FULL_ITEMS.filter((i) => i.key !== "district" || districtApplies);
  const missing = items.filter((i) => !checks[i.key]);
  return { done: items.length - missing.length, total: items.length, missing };
}
